import { type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useSmartOfferRedesignState } from '@/hooks/use-smart-offer-redesign';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { DealState } from '@/lib/graphql/generated/graphql';
import { Loader2 } from 'lucide-react';

export const REDESIGN_WAIT_MS = 3_000;

export type FlowGuardPage = 'smartOffer' | 'csv';

export type FlowGuardInput = {
  page: FlowGuardPage;
  isInternalUser: boolean;
  redesign: boolean | null;
  redesignTimedOut: boolean;
  isOfferControlFlow: boolean;
  closed: boolean;
};

export type FlowGuardDecision =
  | { kind: 'render' }
  | { kind: 'wait' }
  | { kind: 'redirect'; to: 'csv' | 'start' };

export function resolveFlowGuard(input: FlowGuardInput): FlowGuardDecision {
  if (input.page === 'csv') {
    return input.isOfferControlFlow
      ? { kind: 'render' }
      : { kind: 'redirect', to: 'start' };
  }
  if (!input.isInternalUser || input.closed || !input.isOfferControlFlow) {
    return { kind: 'render' };
  }
  if (input.redesign === true) return { kind: 'redirect', to: 'csv' };
  if (input.redesign === null && !input.redesignTimedOut) {
    return { kind: 'wait' };
  }
  return { kind: 'render' };
}

function FlowGuardLoader() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function FlowGuard({
  page,
  children,
}: {
  page: FlowGuardPage;
  children: ReactNode;
}) {
  const { deal } = useDealContext();
  const user = useUser();
  const redesign = useSmartOfferRedesignState();
  const [redesignTimedOut, setRedesignTimedOut] = useState(false);
  const decision = resolveFlowGuard({
    page,
    isInternalUser: user?.isInternalUser === true,
    redesign,
    redesignTimedOut,
    isOfferControlFlow: deal.isOfferControlFlow,
    closed: deal.state === DealState.NegotiationClosed,
  });
  const waiting = decision.kind === 'wait';

  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setRedesignTimedOut(true), REDESIGN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);

  if (waiting) return <FlowGuardLoader />;
  if (decision.kind === 'redirect') {
    return <Navigate to={`/deal/${deal.id}/${decision.to}`} replace />;
  }
  return <>{children}</>;
}

export function SmartOfferFlowGuard({ children }: { children: ReactNode }) {
  return <FlowGuard page="smartOffer">{children}</FlowGuard>;
}

export function CsvFlowGuard({ children }: { children: ReactNode }) {
  return <FlowGuard page="csv">{children}</FlowGuard>;
}
